import React from 'react';
import moment from 'moment';
import { Button } from 'antd';
import { AssetItemDto, AssetVersionItemDto } from '@api/Api';
import Loader from '@components/Loader';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import { toggleUploadAssetModal } from '@redux/actions/modalAction';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';

import { ReactComponent as CheckSvg } from '@assets/icons/check.svg';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';
import { ReactComponent as FileFillSvg } from '@assets/icons/MediaViewer/file-fill.svg';

import './UnconfirmedComparison.less';

type UnconfirmedComparisonProps = {
  selectedVersion: AssetVersionItemDto | null;
  versions: AssetVersionItemDto[];
  asset: AssetItemDto;
  onChangeVersion(version: AssetVersionItemDto): void;
  text?: string;
  showLoader?: boolean;
};

function UnconfirmedComparison({
  versions,
  asset,
  selectedVersion,
  text,
  showLoader,
  onChangeVersion
}: UnconfirmedComparisonProps) {
  const dispatch = useDispatch();

  const preparePreview = (asset: AssetVersionItemDto) =>
    assetPreviewThumbnails({
      url: asset.metadata.preview?.url || '',
      status: asset.metadata.preview?.status || '',
      type: asset.metadata.type || ''
    });

  const onUploadNew = () => {
    dispatch(
      toggleUploadAssetModal({
        visible: true,
        newVersionFromMediaViewer: true,
        workspaceId: asset.workspaceId
      })
    );
  };

  return (
    <div
      className={classNames('compare-versions_unconfirmed', {
        'compare-versions_unconfirmed--loading': showLoader
      })}
    >
      {showLoader && (
        <>
          <Loader
            classNameWrapper="compare-versions_unconfirmed-loader"
            spinSize={40}
            isSpinning={true}
          />
          {text && (
            <div className="compare-versions_unconfirmed-description">
              {text}
            </div>
          )}
        </>
      )}
      {!showLoader && (
        <>
          <div className="compare-versions_unconfirmed-icon">
            <FileFillSvg />
          </div>
          <div className="compare-versions_unconfirmed-title">
            Unsupported comparison
          </div>
          {text && (
            <div className="compare-versions_unconfirmed-description">
              {text}
            </div>
          )}
          {!!versions?.length && (
            <div className="compare-versions_unconfirmed-versions">
              <div className="compare-versions_unconfirmed-title">
                Select version
              </div>
              <div className="compare-versions_unconfirmed-versions-list">
                {versions.map((version) => {
                  const { id, name, createdAt, versionNumber } = version;
                  const date = moment(new Date(createdAt)).format(
                    moment(createdAt).minutes() === 59
                      ? 'MMM DD'
                      : 'MMM DD, h:mma'
                  );
                  const isSelected = selectedVersion?.id === id;
                  return (
                    <div
                      key={id}
                      className={classNames(
                        'compare-versions_unconfirmed-item',
                        {
                          'b-selected': isSelected
                        }
                      )}
                      onClick={() => {
                        onChangeVersion(version);
                      }}
                    >
                      <div className="compare-versions_unconfirmed-image">
                        {preparePreview(version) === 'pending' && (
                          <Loader
                            classNameWrapper="assets-loader-wrapper"
                            spinSize={24}
                            isSpinning={true}
                          />
                        )}
                        {preparePreview(version) !== 'pending' &&
                        typeof preparePreview(version) === 'string' ? (
                          <img
                            src={`${preparePreview(version)}`}
                            alt=""
                            className="assets_user_image"
                          />
                        ) : (
                          preparePreview(version)
                        )}
                      </div>
                      <div className="compare-versions_unconfirmed-content">
                        <div className="compare-versions_unconfirmed-top">
                          <div className="compare-versions_unconfirmed-name">
                            {name}
                          </div>
                          <div className="compare-versions_unconfirmed-label">
                            V{versionNumber}
                          </div>
                          {isSelected && <CheckSvg className="check-svg" />}
                        </div>
                        <div className="compare-versions_unconfirmed-date">
                          {date}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {asset?.asset?.permissions?.uploadNewVersion && (
            <Button
              className="compare-versions_unconfirmed-btn"
              icon={<PlusSvg />}
              htmlType="button"
              onClick={onUploadNew}
            >
              Upload new
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export default UnconfirmedComparison;
